import { settingsDefaults, settingProperties as schemaProperties } from "./widgetSettings";
import { WidgetConfiguration } from "./WidgetConfiguration";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Ref } from "react";
import type { ConversationComposerHandle } from "../chat/conversationComposer";
import { AppIcon } from "@apteva/ui-kit";
import { auth, type DashboardWidgetSize } from "../../api";
import { useOptionalAuth } from "../../hooks/useAuth";
import { usePanelEvents } from "../../hooks/usePanelEvents";
import {
  ChatComponentMount,
  componentAllowedInSlot,
  type InstalledAppRow,
  type UIComponentSpec,
  useInstalledApps,
} from "./chatComponents";
import {
  createWidgetActionBridge,
  type WidgetActionBridge,
  type WidgetContext,
} from "./widgetContext";

export type WidgetSize = DashboardWidgetSize;

/** Widget widths in quarters of the canvas, narrowest first. */
export const WIDGET_SIZES: WidgetSize[] = ["quarter", "half", "three-quarter", "full"];

export const WIDGET_SIZE_LABELS: Record<WidgetSize, string> = {
  quarter: "1/4",
  half: "1/2",
  "three-quarter": "3/4",
  full: "Full",
};

export const WIDGET_SIZE_NAMES: Record<WidgetSize, string> = {
  quarter: "Quarter width",
  half: "Half width",
  "three-quarter": "Three-quarter width",
  full: "Full width",
};

/** One column on phones, two from md, four from xl. */
export const WIDGET_CANVAS_GRID = "grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-4";

// Literal class names so Tailwind generates them. Half keeps the old xl
// footprint (one of two columns); quarters pair up below xl.
const WIDGET_SPAN_CLASSES: Record<WidgetSize, string> = {
  quarter: "",
  half: "md:col-span-2",
  "three-quarter": "md:col-span-2 xl:col-span-3",
  full: "md:col-span-2 xl:col-span-4",
};

export function isWidgetSize(value: unknown): value is WidgetSize {
  return WIDGET_SIZES.includes(value as WidgetSize);
}

export function widgetSpanClass(size: WidgetSize): string {
  return WIDGET_SPAN_CLASSES[size] ?? WIDGET_SPAN_CLASSES.half;
}

export interface WidgetInstance {
  placement?: "assistant" | "main" | "activity" | "details";
  agent_id?: number;
  setup?: import("../../api").PresetConnectionSetup[];
  /** Stable identity so one component may be added more than once. */
  id: string;
  /** app:component manifest identity. */
  component: string;
  size: WidgetSize;
  settings?: Record<string, unknown>;
}

type StoredWidget = string | WidgetInstance;

export interface ProjectUILayout {
  /** String entries are the legacy enabled-component format. */
  slots?: Record<string, StoredWidget[]>;
  sidebar?: string[];
  /** User-created pages belonging to this project. */
  pages?: WorkspacePage[];
}

export interface WorkspacePage {
  id: string;
  title: string;
  icon?: string;
  kind: "system" | "template" | "custom";
  layout?: "grid" | "workspace";
  scope: "project" | "global";
  created_at?: string;
  description?: string;
  pinned?: boolean;
}

export interface UILayoutDocument {
  projects?: Record<string, ProjectUILayout>;
  /** Widgets shown when Home is scoped to the whole workspace. */
  global?: ProjectUILayout;
}

export function workspacePages(document: UILayoutDocument, projectId?: string | null): WorkspacePage[] {
  const projectPages = projectId ? document.projects?.[projectId]?.pages || [] : [];
  const globalPages = document.global?.pages || [];
  return [...(Array.isArray(globalPages) ? globalPages : []), ...(Array.isArray(projectPages) ? projectPages : [])].filter((page) => page && typeof page.id === "string" && typeof page.title === "string");
}

const LAYOUT_EVENT = "apteva:ui-layout-changed";
const surfaceSaveQueues = new Map<string, Promise<unknown>>();
const surfaceSaveVersions = new Map<string, number>();
const sidebarSaveQueues = new Map<string, Promise<unknown>>();
const sidebarSaveVersions = new Map<string, number>();
const pendingSidebars = new Map<string, { projectId: string; names: string[] }>();
const latestLayouts = new Map<number, { document: UILayoutDocument; confirmed: UILayoutDocument; revision: number }>();

function normalizeLayout(value: unknown): UILayoutDocument {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as UILayoutDocument;
}

/** The latest layout from a save another session beat (HTTP 409). */
function layoutConflict(error: unknown): { document: UILayoutDocument; revision: number } | null {
  const failure = error as { status?: number; body?: { ui_layout?: unknown; revision?: unknown } } | undefined;
  if (failure?.status !== 409 || typeof failure.body?.revision !== "number") return null;
  return { document: normalizeLayout(failure.body.ui_layout), revision: failure.body.revision };
}

/** Key-order independent JSON, to compare a surface with the server's copy. */
function canonicalJSON(value: unknown): string {
  return JSON.stringify(value ?? null, (_key, item) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)))
      : item);
}

function withPendingSidebars(document: UILayoutDocument, userId?: number): UILayoutDocument {
  if (!userId) return document;
  let next = document;
  for (const [key, pending] of pendingSidebars) {
    if (!key.startsWith(`${userId}:`)) continue;
    const projects = next.projects || {};
    next = { ...next, projects: {
      ...projects,
      [pending.projectId]: { ...(projects[pending.projectId] || {}), sidebar: pending.names },
    } };
  }
  return next;
}

export function contributionKey(appName: string, componentName: string) {
  return `${appName}:${componentName}`;
}

export function useProjectUILayout(projectId?: string | null, layoutScope: "project" | "global" = "project") {
  const user = useOptionalAuth()?.user;
  const userId = user && typeof user === "object" ? user.id : undefined;
  const profileDocument = user && typeof user === "object" ? normalizeLayout(user.uiLayout) : {};
  const profileRevision = user && typeof user === "object" ? user.uiLayoutRevision : 0;
  const cached = userId ? latestLayouts.get(userId) : undefined;
  const useCached = !!cached && cached.revision >= profileRevision;
  const initial = withPendingSidebars(useCached ? cached.document : profileDocument, userId);
  const initialConfirmed = useCached ? cached.confirmed : profileDocument;
  const initialRevision = useCached ? cached.revision : profileRevision;
  const [document, setDocument] = useState<UILayoutDocument>(initial);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error" | "conflict">("idle");
  const documentRef = useRef(document);
  const confirmedDocumentRef = useRef(initialConfirmed);
  const revisionRef = useRef(initialRevision);

  const applyDocument = useCallback((next: UILayoutDocument, revision?: number) => {
    if (revision != null && revision <= revisionRef.current) return false;
    if (revision != null) {
      revisionRef.current = revision;
      confirmedDocumentRef.current = next;
    }
    const visible = withPendingSidebars(next, userId);
    documentRef.current = visible;
    setDocument(visible);
    if (userId) latestLayouts.set(userId, { document: visible, confirmed: confirmedDocumentRef.current, revision: revisionRef.current });
    return true;
  }, [userId]);

  useEffect(() => {
    if (user && typeof user === "object") {
      applyDocument(normalizeLayout(user.uiLayout), user.uiLayoutRevision);
    }
  }, [applyDocument, user]);
  useEffect(() => {
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<UILayoutDocument | { document: UILayoutDocument; revision?: number }>).detail;
      if (!detail) return;
      if ("document" in detail) applyDocument(detail.document, detail.revision);
      else applyDocument(detail);
    };
    window.addEventListener(LAYOUT_EVENT, onChanged);
    return () => window.removeEventListener(LAYOUT_EVENT, onChanged);
  }, [applyDocument]);

  const publishDocument = useCallback((next: UILayoutDocument, revision?: number) => {
    if (!applyDocument(next, revision)) return;
    window.dispatchEvent(new CustomEvent(LAYOUT_EVENT, {
      detail: { document: next, revision },
    }));
  }, [applyDocument]);

  /** Record a saved revision without replacing newer optimistic edits on screen. */
  const acknowledgeDocument = useCallback((next: UILayoutDocument, revision: number) => {
    if (revision <= revisionRef.current) return;
    revisionRef.current = revision;
    confirmedDocumentRef.current = next;
    if (userId) latestLayouts.set(userId, { document: documentRef.current, confirmed: next, revision });
  }, [userId]);

  const project = layoutScope === "global"
    ? document.global || {}
    : projectId ? document.projects?.[projectId] || {} : {};
  const toggleSidebarApp = useCallback(async (appName: string, defaults: string[]) => {
    if (!projectId || layoutScope !== "project") return;
    const key = `${user && typeof user === "object" ? user.id : 0}:${projectId}`;
    const current = documentRef.current;
    const currentProject = current.projects?.[projectId] || {};
    const base = Object.prototype.hasOwnProperty.call(currentProject, "sidebar")
      ? currentProject.sidebar || [] : defaults;
    const names = [...new Set(base)];
    const pinned = !names.includes(appName);
    const nextNames = pinned ? [...names, appName] : names.filter((name) => name !== appName);
    pendingSidebars.set(key, { projectId, names: nextNames });
    const next: UILayoutDocument = { ...current, projects: {
      ...(current.projects || {}),
      [projectId]: { ...currentProject, sidebar: nextNames },
    } };
    publishDocument(next);
    setSaveState("saving");

    const version = (sidebarSaveVersions.get(key) || 0) + 1;
    sidebarSaveVersions.set(key, version);
    const previous = sidebarSaveQueues.get(key) || Promise.resolve();
    const request = previous.catch(() => undefined).then(() => auth.patchSidebarApp(projectId, appName, pinned, defaults));
    sidebarSaveQueues.set(key, request);
    try {
      const response = await request;
      if (sidebarSaveVersions.get(key) === version) {
        pendingSidebars.delete(key);
        publishDocument(normalizeLayout(response.ui_layout), response.revision);
        setSaveState("saved");
      }
    } catch {
      if (sidebarSaveVersions.get(key) === version) {
        pendingSidebars.delete(key);
        publishDocument(confirmedDocumentRef.current);
        try {
          const latest = await auth.me();
          publishDocument(normalizeLayout(latest.ui_layout), latest.ui_layout_revision);
        } catch { /* Keep the last visible layout until another refresh. */ }
        setSaveState("error");
      }
    } finally {
      if (sidebarSaveQueues.get(key) === request) sidebarSaveQueues.delete(key);
    }
  }, [layoutScope, projectId, publishDocument, user]);

  const updateSurface = useCallback(
    async (surface: string, widgets: WidgetInstance[], options?: {rejectOnError?:boolean}) => {
      if (layoutScope === "project" && !projectId) {
        setSaveState("error");
        if (options?.rejectOnError) throw new Error("Choose a project before changing this layout.");
        return;
      }
      const stored = serializeWidgetInstances(widgets);
      const current = documentRef.current;
      const scopeOf = (document: UILayoutDocument) => layoutScope === "global"
        ? document.global
        : document.projects?.[projectId!];
      const currentProject = scopeOf(current) || {};
      // The surface this edit started from. If another session saved in the
      // meantime but left this surface alone, the save is retried.
      const base = canonicalJSON(currentProject.slots?.[surface]);
      const nextProject: ProjectUILayout = {
        ...currentProject,
        slots: { ...(currentProject.slots || {}), [surface]: stored },
      };
      const optimistic: UILayoutDocument = layoutScope === "global"
        ? { ...current, global: nextProject }
        : { ...current, projects: { ...(current.projects || {}), [projectId!]: nextProject } };
      publishDocument(optimistic);
      setSaveState("saving");
      const queueKey = `${layoutScope}:${projectId || "global"}:${surface}`;
      const version = (surfaceSaveVersions.get(queueKey) || 0) + 1;
      surfaceSaveVersions.set(queueKey, version);
      const previous = surfaceSaveQueues.get(queueKey) || Promise.resolve();
      const save = async () => {
        let expected = revisionRef.current;
        for (let attempt = 0; ; attempt++) {
          try {
            return layoutScope === "global"
              ? await auth.patchGlobalUILayoutSurface(surface, stored, expected)
              : await auth.patchUILayoutSurface(projectId!, surface, stored, expected);
          } catch (error) {
            const conflict = layoutConflict(error);
            if (!conflict || attempt >= 2 || canonicalJSON(scopeOf(conflict.document)?.slots?.[surface]) !== base) throw error;
            expected = conflict.revision;
            acknowledgeDocument(conflict.document, conflict.revision);
          }
        }
      };
      const request = previous.catch(() => undefined).then(save);
      surfaceSaveQueues.set(queueKey, request);
      try {
        const response = await request;
        const confirmed = normalizeLayout(response.ui_layout);
        if (surfaceSaveVersions.get(queueKey) === version) {
          publishDocument(confirmed, response.revision);
          setSaveState("saved");
        } else {
          acknowledgeDocument(confirmed, response.revision);
        }
      } catch (error) {
        if (surfaceSaveVersions.get(queueKey) === version) {
          const latest=documentRef.current;
          const rollback=layoutScope==="global"?confirmedDocumentRef.current.global:confirmedDocumentRef.current.projects?.[projectId!];
          const currentScope=layoutScope==="global"?latest.global:latest.projects?.[projectId!];
          const restored={...currentScope,slots:{...currentScope?.slots}};
          if(rollback?.slots && Object.hasOwn(rollback.slots,surface))restored.slots[surface]=rollback.slots[surface];else delete restored.slots[surface];
          publishDocument(layoutScope==="global"?{...latest,global:restored}:{...latest,projects:{...latest.projects,[projectId!]:restored}});
          // Another session changed this surface: show its layout, not ours.
          const conflict = layoutConflict(error);
          if (conflict) publishDocument(conflict.document, conflict.revision);
          setSaveState(conflict ? "conflict" : "error");
        }
        if(options?.rejectOnError)throw error;
      } finally {
        if (surfaceSaveQueues.get(queueKey) === request) surfaceSaveQueues.delete(queueKey);
      }
    },
    [acknowledgeDocument, layoutScope, projectId, publishDocument],
  );

  return { document, project, toggleSidebarApp, updateSurface, saveState };
}

export interface Contribution {
  app: InstalledAppRow;
  spec: UIComponentSpec;
  key: string;
}

export type DashboardScope = "project" | "global";

export { supportsDashboardScope } from "./chatComponents";

export interface ResolvedWidgetInstance extends WidgetInstance {
  contribution: Contribution;
}

export function contributionsFor(
  apps: InstalledAppRow[],
  slot: string,
  scope: DashboardScope = "project",
): Contribution[] {
  const out: Contribution[] = [];
  for (const app of apps) {
    // An upgrade reports `pending` while the previous version keeps serving.
    // Keep its widgets mounted so routine upgrades do not collapse the grid.
    if (app.status && app.status !== "running" && !app.serving) continue;
    for (const spec of app.ui_components || []) {
      if (!spec.slots || !componentAllowedInSlot(spec, slot, scope)) continue;
      out.push({ app, spec, key: contributionKey(app.name, spec.name) });
    }
  }
  return out;
}

interface ContributionEligibilityResponse {
  contributions?: Array<{
    app: string;
    component: string;
    eligible: boolean;
  }>;
}

export async function fetchEligibleContributionKeys(
  projectId: string,
  slot: string,
  agentId: number,
  threadId?: string,
  signal?: AbortSignal,
): Promise<Set<string>> {
  const params = new URLSearchParams({
    project_id: projectId,
    surface: slot,
    agent_id: String(agentId),
  });
  if (threadId) params.set("thread_id", threadId);
  const response = await fetch(`/api/ui/contributions?${params.toString()}`, {
    credentials: "same-origin",
    signal,
  });
  if (!response.ok) throw new Error("unable to resolve contributions");
  const payload = await response.json() as ContributionEligibilityResponse;
  return new Set(
    (payload.contributions || [])
      .filter((item) => item.eligible)
      .map((item) => contributionKey(item.app, item.component)),
  );
}

export function useEligibleContributionKeys(
  projectId: string | null | undefined,
  slot: string,
  agentId?: number,
  threadId?: string,
) {
  const [keys, setKeys] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (!projectId || !agentId || slot === "dashboard.home") {
      setKeys(null);
      return;
    }
    let cancelled = false;
    const load = () =>
      fetchEligibleContributionKeys(projectId, slot, agentId, threadId)
        .then((eligible) => {
          if (cancelled) return;
          setKeys(eligible);
        })
        .catch(() => {
          if (!cancelled) setKeys(null);
        });
    void load();
    const onAppsChanged = () => void load();
    window.addEventListener("apteva:apps-changed", onAppsChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("apteva:apps-changed", onAppsChanged);
    };
  }, [agentId, projectId, slot, threadId]);
  return keys;
}

export function supportedWidgetSizes(spec: UIComponentSpec): WidgetSize[] {
  const declared = new Set((spec.supported_sizes || []).filter(isWidgetSize));
  // A widget that works at half and full width also works in between.
  if (declared.has("half") && declared.has("full")) declared.add("three-quarter");
  if (declared.size) return WIDGET_SIZES.filter((size) => declared.has(size));
  // Legacy components stay fixed at the width their manifest requested.
  return [spec.default_width === 2 ? "full" : "half"];
}

export function defaultWidgetSize(spec: UIComponentSpec): WidgetSize {
  const sizes = supportedWidgetSizes(spec);
  if (spec.default_size && sizes.includes(spec.default_size)) return spec.default_size;
  return sizes.includes("half") ? "half" : sizes[0];
}

function normalizedWidgetSize(
  spec: UIComponentSpec,
  requested?: string,
): WidgetSize {
  const sizes = supportedWidgetSizes(spec);
  return requested && sizes.includes(requested as WidgetSize)
    ? (requested as WidgetSize)
    : defaultWidgetSize(spec);
}

export function defaultWidgetSettings(
  spec: UIComponentSpec,
): Record<string, unknown> {
  return settingsDefaults(spec.settings_schema);
}

export function widgetInstancesFor(
  contributions: Contribution[],
  slot: string,
  project: ProjectUILayout,
): ResolvedWidgetInstance[] {
  const byKey = new Map(contributions.map((item) => [item.key, item]));
  return storedWidgetInstancesFor(contributions, slot, project).flatMap((entry) => {
    const contribution = byKey.get(entry.component);
    return contribution ? [{ ...entry, contribution }] : [];
  });
}

export function storedWidgetInstancesFor(
  contributions: Contribution[],
  slot: string,
  project: ProjectUILayout,
): WidgetInstance[] {
  const byKey = new Map(contributions.map((item) => [item.key, item]));
  const explicit =
    project.slots && Object.prototype.hasOwnProperty.call(project.slots, slot);
  const explicitValue = project.slots?.[slot];
  const stored: StoredWidget[] = explicit
    ? Array.isArray(explicitValue) ? explicitValue : []
    : slot === "dashboard.home"
      ? []
      : contributions
        .filter((item) => item.spec.suggested)
        .map((item) => ({
          id: `suggested:${item.key}`,
          component: item.key,
          size: defaultWidgetSize(item.spec),
          settings: defaultWidgetSettings(item.spec),
        }));

  return stored.flatMap((entry, index) => {
    if (
      typeof entry !== "string" &&
      (!entry || typeof entry !== "object" || Array.isArray(entry))
    ) return [];
    const legacy = typeof entry === "string";
    const component = legacy ? entry : entry.component;
    const contribution = byKey.get(component);
    const fallbackSize: WidgetSize = typeof entry !== "string" && isWidgetSize(entry.size)
      ? entry.size
      : "half";
    return [{
      id: legacy
        ? `legacy:${slot}:${index}:${component}`
        : entry.id || `widget:${slot}:${index}:${component}`,
      component,
      agent_id: legacy ? undefined : entry.agent_id,
      setup: legacy ? undefined : entry.setup,
      size: contribution
        ? normalizedWidgetSize(contribution.spec, legacy ? undefined : entry.size)
        : fallbackSize,
      settings: legacy
        ? contribution ? defaultWidgetSettings(contribution.spec) : {}
        : { ...(contribution ? defaultWidgetSettings(contribution.spec) : {}), ...(entry.settings || {}) },
    }];
  });
}

export function enabledContributionKeys(
  contributions: Contribution[],
  slot: string,
  project: ProjectUILayout,
): string[] {
  return widgetInstancesFor(contributions, slot, project).map(
    (instance) => instance.component,
  );
}

export function serializeWidgetInstances(
  instances: WidgetInstance[],
): WidgetInstance[] {
  return instances.map((item) => ({
    id: item.id,
    component: item.component,
    size: item.size,
    ...(item.placement ? { placement: item.placement } : {}),
    ...(item.agent_id !== undefined ? { agent_id: item.agent_id } : {}),
    ...(item.setup?.length ? { setup: item.setup } : {}),
    ...(item.settings && Object.keys(item.settings).length
      ? { settings: item.settings }
      : {}),
  }));
}

export function reorderWidgetInstances(
  instances: WidgetInstance[],
  id: string,
  targetID: string,
): WidgetInstance[] {
  const next = serializeWidgetInstances(instances);
  if (id === targetID) return next;
  const from = next.findIndex((item) => item.id === id);
  const to = next.findIndex((item) => item.id === targetID);
  if (from < 0 || to < 0) return next;
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function preferredSidebarAppNames<
  T extends { name: string; suggested?: boolean },
>(apps: T[], project: ProjectUILayout): string[] {
  return Object.prototype.hasOwnProperty.call(project, "sidebar")
    ? project.sidebar || []
    : apps.filter((app) => app.suggested).map((app) => app.name);
}

export function AppContributionArea({
  slot,
  projectId,
  agentId,
  threadId,
  className = "contents",
  empty = null,
}: {
  slot: string;
  projectId?: string | null;
  agentId?: number;
  threadId?: string;
  className?: string;
  empty?: React.ReactNode;
}) {
  const apps = useInstalledApps(projectId);
  const { project } = useProjectUILayout(projectId);
  const eligibleKeys = useEligibleContributionKeys(
    projectId,
    slot,
    agentId,
    threadId,
  );
  const contributions = useMemo(
    () => contributionsFor(apps, slot).filter(
      (item) => !eligibleKeys || eligibleKeys.has(item.key),
    ),
    [apps, eligibleKeys, slot],
  );
  const widgets = widgetInstancesFor(contributions, slot, project);
  if (!projectId || widgets.length === 0) return empty;
  return (
    <div className={className}>
      {widgets.map((instance) => (
        <ContributionMount
          key={instance.id}
          instance={instance}
          apps={apps}
          slot={slot}
          projectId={projectId}
          agentId={agentId}
          threadId={threadId}
        />
      ))}
    </div>
  );
}

export function ContributionMount({
  composerRef,
  pageContext,
  instance,
  apps,
  slot,
  projectId,
  dashboardScope = "project",
  agentId,
  threadId,
  workspaceContext,
  widgetContext,
  widgetActions,
}: {
  /** Optional Conversations contract; omitted for all other widgets. */
  composerRef?: Ref<ConversationComposerHandle>;
  pageContext?: import("../chat/pageContext").AssistantPageContext;
  instance: ResolvedWidgetInstance;
  apps: InstalledAppRow[];
  slot: string;
  projectId?: string;
  dashboardScope?: DashboardScope;
  agentId?: number;
  threadId?: string;
  workspaceContext?: { app: string; kind: string; id: string };
  /** Optional shared host context. Existing app widgets do not need it. */
  widgetContext?: WidgetContext;
  /** Optional shared host action bridge. Existing app widgets do not need it. */
  widgetActions?: WidgetActionBridge;
}) {
  const { contribution } = instance;
  const events = usePanelEvents(contribution.app.name, projectId, contribution.app.install_id, contribution.spec.refresh_topics || [], dashboardScope);
  const width = widgetSpanClass(instance.size);
  const effectiveContext: WidgetContext = {
    projectId,
    scope: dashboardScope,
    ...widgetContext,
    agentId: agentId ?? instance.agent_id ?? widgetContext?.agentId,
    threadId: threadId ?? widgetContext?.threadId,
  };
  const effectiveActions = widgetActions || createWidgetActionBridge();
  return (
    <div
      className={`h-full min-w-0 w-full ${width}`}
      data-app-contribution={contribution.key}
      data-widget-id={instance.id}
      data-widget-size={instance.size}
    >
      <ChatComponentMount
        comp={{
          app: contribution.app.name,
          name: contribution.spec.name,
          props: {
            agentId: agentId ?? instance.agent_id,
            instanceId: agentId ?? instance.agent_id,
            threadId,
            ...(workspaceContext ? { workspaceContext } : {}),
            ...events,
            slot,
            widgetId: instance.id,
            widgetSize: instance.size,
            widgetSettings: instance.settings || {},
            pageContext,
            ...(composerRef ? { composerRef } : {}),
                      widgetContext: effectiveContext,
            widgetActions: effectiveActions,
          },
        }}
        widgetContext={effectiveContext}
        widgetActions={effectiveActions}
        apps={apps}
        projectId={projectId}
        dashboardScope={dashboardScope}
        slot={slot}
      />
    </div>
  );
}

export function ContributionManager({
  slot,
  projectId,
  label = "Customize",
  agentId,
  threadId,
  compact = false,
}: {
  slot: string;
  projectId?: string | null;
  label?: string;
  agentId?: number;
  threadId?: string;
  compact?: boolean;
}) {
  const apps = useInstalledApps(projectId);
  const { project, updateSurface } = useProjectUILayout(projectId);
  const [open, setOpen] = useState(false);
  const [configuration,setConfiguration]=useState<{scope:string;item:Contribution;instance:WidgetInstance;adding:boolean}|null>(null);
  const configurationScope=JSON.stringify([projectId,slot,agentId,threadId]);
  const [dragging, setDragging] = useState<string | null>(null);
  const eligibleKeys = useEligibleContributionKeys(projectId, slot, agentId, threadId);
  const options = useMemo(
    () => contributionsFor(apps, slot).filter(
      (item) => !eligibleKeys || eligibleKeys.has(item.key),
    ),
    [apps, eligibleKeys, slot],
  );
  if (!projectId) return null;
  const configuredAll = storedWidgetInstancesFor(options, slot, project);
  const configured = widgetInstancesFor(options, slot, project);
  const persist = (next: WidgetInstance[],strict=false) => updateSurface(slot,next,{rejectOnError:strict});
  const add = (item: Contribution) => {
    const instance={id:newWidgetInstanceID(item.key),component:item.key,size:defaultWidgetSize(item.spec),settings:defaultWidgetSettings(item.spec)};
    if(Object.keys(item.spec.settings_schema?.properties||{}).length){setConfiguration({scope:configurationScope,item,instance,adding:true});return;}
    void persist([...configuredAll,instance]);
  };
  const patchInstance=(id:string,patch:Partial<WidgetInstance>)=>persist(configuredAll.map(item=>item.id===id?{...item,...patch}:item));
  const remove = (id: string) =>
    persist(configuredAll.filter((item) => item.id !== id));
  const move = (id: string, targetID: string) => {
    persist(reorderWidgetInstances(configuredAll, id, targetID));
  };
  const nudge = (id: string, delta: number) => {
    const from = configured.findIndex((item) => item.id === id);
    const to = Math.max(0, Math.min(configured.length - 1, from + delta));
    if (from < 0 || from === to) return;
    move(id, configured[to].id);
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={compact ? "Add or manage widgets" : undefined}
        className={compact
          ? "flex h-8 min-w-8 items-center justify-center rounded-md border border-border px-2 text-sm text-text-muted hover:border-accent hover:text-accent"
          : "rounded border border-border px-3 py-1.5 text-[10px] text-text-muted hover:bg-bg-hover hover:text-text"}
      >
        {label}
      </button>
      {configuration && configuration.scope===configurationScope && options.some(item=>item.key===configuration.item.key&&item.app.install_id===configuration.item.app.install_id) && <WidgetConfiguration
        key={`${configurationScope}:${configuration.instance.id}:${configuration.item.app.install_id}`}
        label={configuration.item.spec.label||configuration.item.spec.name} schema={configuration.item.spec.settings_schema} ui={configuration.item.spec.settings_ui}
        settings={configuration.instance.settings||{}} app={configuration.item.app.name} installId={configuration.item.app.install_id} projectId={projectId} adding={configuration.adding}
        renderPreview={(settings,ctx)=><ContributionMount instance={{...configuration.instance,settings,contribution:configuration.item}} apps={apps} slot={slot} projectId={projectId} agentId={agentId} threadId={threadId} widgetContext={ctx.context} widgetActions={ctx.actions}/>}
        onSave={settings=>persist(configuration.adding?[...configuredAll,{...configuration.instance,settings}]:configuredAll.map(widget=>widget.id===configuration.instance.id?{...widget,settings}:widget),true)}
        onClose={()=>setConfiguration(null)}
      />}
      {open && (
        <div
          className="fixed inset-0 z-[100] grid place-items-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Customize dashboard"
        >
          <button
            className="absolute inset-0 bg-black/65"
            onClick={() => setOpen(false)}
            aria-label="Close"
          />
          <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded border border-border bg-bg-card shadow-2xl">
            <header className="flex items-center border-b border-border px-5 py-4">
              <div>
                <h2 className="text-sm font-bold text-text">Customize widgets</h2>
                <p className="mt-1 text-[10px] text-text-dim">
                  Add, resize, configure, and reorder components from installed apps.
                </p>
              </div>
              <button
                className="ml-auto text-lg text-text-dim hover:text-text"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </header>
            <div className="min-h-0 flex-1 overflow-auto p-5">
              <div className="mb-3 flex items-center text-[9px] font-bold uppercase tracking-wide text-text-dim">
                <span>Your widgets</span>
                <span className="ml-auto">{configured.length}</span>
              </div>
              <div className="space-y-3">
                {configured.length === 0 && (
                  <div className="rounded border border-dashed border-border px-4 py-6 text-center text-[10px] text-text-dim">
                    No widgets in this area. Add one below.
                  </div>
                )}
                {configured.map((instance, index) => {
                  const item = instance.contribution;
                  const sizes = supportedWidgetSizes(item.spec);
                  return (
                    <div
                      key={instance.id}
                      draggable
                      onDragStart={() => setDragging(instance.id)}
                      onDragEnd={() => setDragging(null)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        if (dragging) move(dragging, instance.id);
                        setDragging(null);
                      }}
                      className={`rounded border bg-bg-subtle p-3 ${
                        dragging === instance.id ? "border-accent opacity-60" : "border-border"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          aria-label="Drag to reorder"
                          className="mt-1 cursor-grab text-sm text-text-dim active:cursor-grabbing"
                          title="Drag to reorder"
                        >
                          ⋮⋮
                        </button>
                        <AppIcon
                          name={item.app.display_name || item.app.name}
                          src={item.app.icon}
                          iconStyle={item.app.icon_style}
                          size="sm"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold text-text">
                                {item.spec.label || item.spec.name}
                              </div>
                              <div className="mt-0.5 text-[10px] text-text-dim">
                                {item.spec.description || item.app.display_name || item.app.name}
                              </div>
                            </div>
                            <WidgetFootprintPreview size={instance.size} />
                          </div>
                          {sizes.length > 1 && (
                            <div className="mt-3 flex items-center gap-1" aria-label="Widget size">
                              {sizes.map((size) => (
                                <button
                                  key={size}
                                  type="button"
                                  onClick={() => patchInstance(instance.id, { size })}
                                  aria-label={WIDGET_SIZE_NAMES[size]}
                                  title={WIDGET_SIZE_NAMES[size]}
                                  className={`rounded border px-2.5 py-1 text-[9px] font-bold ${
                                    instance.size === size
                                      ? "border-accent bg-accent/10 text-accent"
                                      : "border-border text-text-muted hover:bg-bg-hover hover:text-text"
                                  }`}
                                >
                                  {WIDGET_SIZE_LABELS[size]}
                                </button>
                              ))}
                            </div>
                          )}
                          {!!Object.keys(item.spec.settings_schema?.properties||{}).length && <button type="button" className="mt-3 min-h-10 rounded-lg border border-border px-3 text-sm" onClick={()=>setConfiguration({scope:configurationScope,item,instance:structuredClone({...instance,contribution:undefined}),adding:false})}>Configure</button>}
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            aria-label="Move widget up"
                            disabled={index === 0}
                            onClick={() => nudge(instance.id, -1)}
                            className="rounded border border-border px-2 py-1 text-[10px] text-text-muted hover:bg-bg-hover disabled:opacity-25"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            aria-label="Move widget down"
                            disabled={index === configured.length - 1}
                            onClick={() => nudge(instance.id, 1)}
                            className="rounded border border-border px-2 py-1 text-[10px] text-text-muted hover:bg-bg-hover disabled:opacity-25"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            aria-label="Remove widget"
                            onClick={() => remove(instance.id)}
                            className="rounded border border-red/40 px-2 py-1 text-[10px] text-red hover:bg-red/10"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mb-3 mt-6 text-[9px] font-bold uppercase tracking-wide text-text-dim">
                Widget gallery
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {options.length === 0 && (
                  <p className="col-span-full rounded border border-dashed border-border px-4 py-6 text-center text-[10px] text-text-dim">
                    No installed app provides a widget for this area yet.
                  </p>
                )}
                {options.map((item) => {
                  const count = configured.filter((instance) => instance.component === item.key).length;
                  return (
                    <div key={item.key} className="flex items-center gap-3 rounded border border-border p-3">
                      <AppIcon
                        name={item.app.display_name || item.app.name}
                        src={item.app.icon}
                        iconStyle={item.app.icon_style}
                        size="sm"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-semibold text-text">
                          {item.spec.label || item.spec.name}
                        </div>
                        <div className="truncate text-[9px] text-text-dim">
                          {item.app.display_name || item.app.name}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => add(item)}
                        className="rounded border border-accent px-2.5 py-1 text-[9px] font-bold text-accent hover:bg-accent/10"
                      >
                        {count ? "Add another" : "Add"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            <footer className="flex justify-end border-t border-border p-4">
              <button
                onClick={() => setOpen(false)}
                className="rounded border border-accent bg-accent/10 px-4 py-2 text-xs font-bold text-accent hover:bg-accent/20"
              >
                Done
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

function newWidgetInstanceID(component: string): string {
  const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${component}:${suffix}`;
}

const FOOTPRINT_QUARTERS: Record<WidgetSize, number> = { quarter: 1, half: 2, "three-quarter": 3, full: 4 };

function WidgetFootprintPreview({ size }: { size: WidgetSize }) {
  const filled = FOOTPRINT_QUARTERS[size] ?? 2;
  return (
    <span
      title={WIDGET_SIZE_NAMES[size]}
      className="grid h-7 w-12 grid-cols-4 gap-0.5 rounded border border-border bg-bg p-1"
      aria-label={`${WIDGET_SIZE_NAMES[size]} preview`}
    >
      {[0, 1, 2, 3].map((quarter) => (
        <span key={quarter} className={`rounded-sm ${quarter < filled ? "bg-accent/45" : "bg-border"}`} />
      ))}
    </span>
  );
}

export { WidgetSettingsEditor } from "./WidgetSettingsEditor";

export function SidebarAppManager({
  projectId,
  apps,
  onClose,
}: {
  projectId: string;
  apps: Array<{
    name: string;
    label: string;
    icon?: string;
    iconStyle?: "image" | "monochrome";
    suggested?: boolean;
  }>;
  onClose: () => void;
}) {
  const { project, toggleSidebarApp, saveState } = useProjectUILayout(projectId);
  const selected = new Set(preferredSidebarAppNames(apps, project));
  const toggle = (name: string) => {
    void toggleSidebarApp(name, apps.filter((app) => app.suggested).map((app) => app.name));
  };
  return (
    <div
      className="fixed inset-0 z-[110] grid place-items-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Preferred apps"
    >
      <button
        className="absolute inset-0 bg-black/65"
        onClick={onClose}
        aria-label="Close"
      />
      <div className="relative w-full max-w-md rounded border border-border bg-bg-card shadow-2xl">
        <header className="flex items-center border-b border-border px-5 py-4">
          <div>
            <h2 className="text-sm font-bold text-text">Preferred apps</h2>
            <p className="mt-1 text-[10px] text-text-dim">
              Choose which app pages stay in the sidebar.
            </p>
          </div>
          <button
            className="ml-auto text-lg text-text-dim hover:text-text"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="max-h-[60vh] divide-y divide-border overflow-auto">
          {apps.map((app) => (
            <label
              key={app.name}
              className="flex cursor-pointer items-center gap-3 px-5 py-3 hover:bg-bg-hover"
            >
              <input
                type="checkbox"
                checked={selected.has(app.name)}
                onChange={() => toggle(app.name)}
                className="accent-accent"
              />
              <AppIcon
                name={app.label}
                src={app.icon}
                iconStyle={app.iconStyle}
                size="sm"
              />
              <span className="text-xs font-semibold text-text">
                {app.label}
              </span>
            </label>
          ))}
        </div>
        <footer className="flex justify-end border-t border-border p-4">
          {saveState === "saving" && <span className="mr-auto self-center text-xs text-text-muted">Saving…</span>}
          {saveState === "error" && <span role="alert" className="mr-auto self-center text-xs text-red">Could not save. Try again.</span>}
          <button
            onClick={onClose}
            className="rounded border border-accent bg-accent/10 px-4 py-2 text-xs font-bold text-accent hover:bg-accent/20"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}
